import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import L from 'leaflet';
import { MapContainer, TileLayer, Polygon, Circle, Marker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { pointInGeoJSON, getBoundsFromPolygon, type RequestAreaGeoJSON } from '../utils/geoUtils';

const DEFAULT_CENTER: [number, number] = [20.5937, 78.9629];
const DEFAULT_ZOOM = 16;
const MAX_ZOOM_ON_LOAD = 19;

// Blink animation: only when outside gate/school; stops when inside
const USER_ICON_BLINK_CSS = `
  .user-location-icon .user-location-icon-inner {
    width: 8px;
    height: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: green;
    border: 1px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  }
  @keyframes user-location-blink-keyframes {
    0% { opacity: 1; }
    50% { opacity: 0; }
    100% { opacity: 1; }
  }
  .user-location-icon.user-location-blink .user-location-icon-inner {
    animation: user-location-blink-keyframes 300ms infinite;
  }
`;

interface GateForMap {
  gateId: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  requestArea: RequestAreaGeoJSON;
}

interface LocationMapDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'check' | 'request';
  schoolGeofence: { type: string; coordinates: number[][][] | number[][][][] } | null;
  gates: GateForMap[];
  /** When set (e.g. in request mode), use this gate's requestArea for inside/outside check and draw it on the map. */
  currentGateId?: string | null;
  requireGeofence: boolean;
  onConfirm?: (latitude: number, longitude: number) => void;
}

function FitBounds({ bounds, userPos }: { bounds: [[number, number], [number, number]] | null; userPos: [number, number] | null }) {
  const map = useMap();
  const fitted = React.useRef(false);
  useEffect(() => {
    if (!map || fitted.current) return;
    fitted.current = true;
    // animate: false avoids _onZoomTransitionEnd running after unmount (e.g. dialog close) which causes _leaflet_pos errors
    const opts = { animate: false } as const;
    if (bounds) {
      let minLat = bounds[0][0], minLng = bounds[0][1], maxLat = bounds[1][0], maxLng = bounds[1][1];
      if (userPos) {
        minLat = Math.min(minLat, userPos[0]); minLng = Math.min(minLng, userPos[1]);
        maxLat = Math.max(maxLat, userPos[0]); maxLng = Math.max(maxLng, userPos[1]);
      }
      map.fitBounds([[minLat, minLng], [maxLat, maxLng]], { maxZoom: MAX_ZOOM_ON_LOAD, padding: [40, 40], ...opts });
    } else if (userPos) {
      map.setView(userPos, MAX_ZOOM_ON_LOAD, opts);
    }
  }, [map, bounds, userPos]);
  return null;
}

export default function LocationMapDialog({
  open,
  onClose,
  mode,
  schoolGeofence,
  gates,
  currentGateId = null,
  requireGeofence,
  onConfirm,
}: LocationMapDialogProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<[number, number] | null>(null);
  const [insideSchool, setInsideSchool] = useState<boolean | null>(null);
  const [showYouAreHereTooltip, setShowYouAreHereTooltip] = useState(false);
  const prevOpenRef = React.useRef(false);

  const currentGate = currentGateId ? gates.find((g) => g.gateId === currentGateId) : null;
  const gateRequestArea: RequestAreaGeoJSON = (() => {
    const ra = currentGate?.requestArea;
    if (!ra) return null;
    if (ra.type === 'Circle') return (ra as { type: 'Circle'; center: [number, number]; radius: number }).center != null && (ra as { radius?: number }).radius != null ? ra : null;
    return (ra as { coordinates?: unknown[] }).coordinates?.length ? ra : null;
  })();

  const checkLocation = useCallback(() => {
    setLoading(true);
    setError(null);
    setPosition(null);
    setInsideSchool(null);
    if (!navigator.geolocation) {
      setError('Location is not supported by your device.');
      setLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPosition([lat, lng]);
        let inside = true;
        if (requireGeofence) {
          if (gateRequestArea) {
            inside = pointInGeoJSON(lng, lat, gateRequestArea);
          } else {
            inside = pointInGeoJSON(lng, lat, schoolGeofence);
          }
        }
        setInsideSchool(inside);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Unable to get your location.');
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  }, [schoolGeofence, requireGeofence, gateRequestArea]);

  // Only run location check when dialog opens (false -> true), not on every checkLocation identity change
  useEffect(() => {
    const didOpen = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (didOpen) checkLocation();
  }, [open, checkLocation]);

  // Show "You are here" tooltip on the marker for a few seconds when map first shows position
  useEffect(() => {
    if (!position || !open) return;
    setShowYouAreHereTooltip(true);
    const t = setTimeout(() => setShowYouAreHereTooltip(false), 4000);
    return () => clearTimeout(t);
  }, [position, open]);

  const bounds = useMemo(() => getBoundsFromPolygon(schoolGeofence), [schoolGeofence]);
  const gateBounds = gateRequestArea ? getBoundsFromPolygon(gateRequestArea) : null;
  const effectiveBounds = useMemo((): [[number, number], [number, number]] | null => {
    if (!bounds && !gateBounds) return null;
    if (!bounds) return gateBounds;
    if (!gateBounds) return bounds;
    return [
      [Math.min(bounds[0][0], gateBounds[0][0]), Math.min(bounds[0][1], gateBounds[0][1])],
      [Math.max(bounds[1][0], gateBounds[1][0]), Math.max(bounds[1][1], gateBounds[1][1])],
    ];
  }, [bounds, gateBounds]);
  let center: [number, number] = DEFAULT_CENTER;
  if (position) center = position;
  else if (bounds) center = [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2];

  const polygonPositions = schoolGeofence?.coordinates
    ? (schoolGeofence.type === 'MultiPolygon'
        ? ((schoolGeofence.coordinates as number[][][][])[0]?.[0] || []).map((p) => [p[1], p[0]] as [number, number])
        : ((schoolGeofence.coordinates as number[][][])[0] || []).map((p) => [p[1], p[0]] as [number, number]))
    : [];

  // Blink when outside gate/school; stop blinking when inside
  const userLocationIcon = useMemo(() => {
    const shouldBlink = insideSchool !== true;
    return L.divIcon({
      className: `user-location-icon${shouldBlink ? ' user-location-blink' : ''}`,
      html: `<div class="user-location-icon-inner"> </div>`,
      iconSize: [8, 8],
      iconAnchor: [0.1, 0.1],
    });
  }, [insideSchool]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', height: '100vh' } }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        {mode === 'check' ? 'Check my location' : 'Confirm location for request'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">Getting your location…</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {!loading && position && open && (
          <>
            <style>{USER_ICON_BLINK_CSS}</style>
            <Box sx={{ flex: 1, minHeight: 0, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
              <MapContainer
                key="location-map"
                {...({
                  center,
                  zoom: DEFAULT_ZOOM,
                  minZoom: DEFAULT_ZOOM,  
                  style: { height: '100%', width: '100%' },
                  scrollWheelZoom: true,
                } as React.ComponentProps<typeof MapContainer>)}
              >
                <TileLayer url="https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png" />
                <FitBounds bounds={effectiveBounds} userPos={position} />
                {polygonPositions.length >= 3 && (
                  <Polygon
                    positions={polygonPositions}
                    pathOptions={{ color: '#4f227d', fillOpacity: 0.2, weight: 2 }}
                  />
                )}
                {gateRequestArea && gateRequestArea.type === 'Circle' ? (
                  <Circle
                    center={[(gateRequestArea as { center: [number, number] }).center[1], (gateRequestArea as { center: [number, number] }).center[0]]}
                    {...( { radius: (gateRequestArea as { radius: number }).radius } as Record<string, unknown> )}
                    pathOptions={{ color: '#e65100', fillColor: '#ff9800', fillOpacity: 0.35, weight: 2 }}
                  />
                ) : (() => {
                  const ring = gateRequestArea && (gateRequestArea as { coordinates?: number[][][] }).coordinates?.[0];
                  return ring && ring.length >= 3 ? (
                    <Polygon
                      positions={ring.map((p) => [p[1], p[0]] as [number, number])}
                      pathOptions={{ color: '#e65100', fillColor: '#ff9800', fillOpacity: 0.35, weight: 2 }}
                    />
                  ) : null;
                })()}
                <Marker
                  position={position}
                  {...({ icon: userLocationIcon, title: 'You are here' } as Record<string, unknown>)}
                >
                  {showYouAreHereTooltip && (
                    <Tooltip {...({ permanent: true } as Record<string, unknown>)}>
                      You are here
                    </Tooltip>
                  )}
                </Marker>
              </MapContainer>
            </Box>
          </>
        )}
        {!loading && position && open && (
          <>
            {requireGeofence && insideSchool !== null && (
              <Alert severity={insideSchool ? 'success' : 'warning'} sx={{ mb: 1 }}>
                {insideSchool
                  ? gateRequestArea
                    ? "You are now inside the geofencing area. You can request for dispersal."
                    : "You are now inside the geofencing area. You can request for dispersal."
                  : gateRequestArea
                    ? 'Come inside the marked gate area (orange). Your current location is outside.'
                    : 'Come inside the marked area. Your current location is outside the school boundary.'}
              </Alert>
            )}
            {!requireGeofence && (
              <Alert severity="info">Location is not required for this school.</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {mode === 'request' && onConfirm && position && (!requireGeofence || insideSchool) && (
          <Button variant="contained" color="primary" onClick={() => onConfirm(position[0], position[1])}>
            Confirm & request
          </Button>
        )}
        {!loading && position && (
          <Button onClick={checkLocation}>Refresh location</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
